const TRACK_DRAG_THRESHOLD = 5; // px of pointer travel before a press becomes a drag

export class ProductInfo extends HTMLElement {
  abortController = undefined;
  swiper = undefined;
  thumbsSwiper = undefined;
  resizeTimeout = undefined;
  lastVerticalThumbs = undefined;
  desktopMediaQuery = window.matchMedia('(min-width: 768px)');

  constructor() {
    super();
    this.onResize = this.onResize.bind(this);
    this.onSizeGuideClick = this.onSizeGuideClick.bind(this);
    this.onProductVariantChange = this.onProductVariantChange.bind(this);
    this.onBackInStockClick = this.onBackInStockClick.bind(this);
  }

  setupEventListeners() {
    this.variantSelector?.addEventListener('change', this.onVariantChange.bind(this));
    if (!this.quantitySelector) return;
    this.quantitySelector.addEventListener('change', this.onQuantitySelectorEvent.bind(this));
    this.quantitySelector.querySelector('button[name="plus"]').addEventListener('click', this.onQuantitySelectorEvent.bind(this));
    this.quantitySelector.querySelector('button[name="minus"]').addEventListener('click', this.onQuantitySelectorEvent.bind(this));
  }

  setupRatingScroll() {
    this.querySelectorAll('.PDP__reviews--scrollable').forEach((el) => {
      const scroll = () => {
        const target = document.getElementById('js-pdp-reviews');
        if (!target) return;
        const headerOffset =
          parseInt(getComputedStyle(document.documentElement).getPropertyValue('--header-height')) || 80;
        const y = target.getBoundingClientRect().top + window.scrollY - headerOffset;
        window.scrollTo({ top: y, behavior: 'smooth' });
      };
      el.addEventListener('click', scroll);
      el.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          scroll();
        }
      });
    });
  }

  connectedCallback() {
    this.setupEventListeners();
    this.setupRatingScroll();
    this.setupSizeGuide();
    this.addEventListener('click', this.onBackInStockClick);
    this.initSwiper();
    this.addEventListener('pointerdown', this.onTrackPointerDown);
    window.addEventListener('resize', this.onResize);
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.onSizeGuideClick);
    this.removeEventListener('pointerdown', this.onTrackPointerDown);
    this.endTrackDrag();
    document.removeEventListener('product:variant-change', this.onProductVariantChange);
    this.removeEventListener('click', this.onBackInStockClick);
    window.removeEventListener('resize', this.onResize);
    clearTimeout(this.resizeTimeout);
    this.destroySwiper();
  }

  /**
   * Lets mouse users drag the image-swatch rail sideways — it is a native
   * horizontal scroller, which only touch and trackpads can move. Delegated from
   * <product-info> because the rail's markup can be replaced on variant change.
   * Touch and pen keep their native scrolling.
   */
  onTrackPointerDown = (event) => {
    if (event.pointerType !== 'mouse' || event.button !== 0) return;

    const track = event.target.closest('.variant-select__images-track');
    if (!track || track.scrollWidth <= track.clientWidth) return;

    this.endTrackDrag();
    this.trackDrag = { track, startX: event.clientX, startScroll: track.scrollLeft, moved: false };
    document.addEventListener('pointermove', this.onTrackPointerMove);
    document.addEventListener('pointerup', this.onTrackPointerUp);
    document.addEventListener('pointercancel', this.onTrackPointerUp);
  };

  onTrackPointerMove = (event) => {
    const drag = this.trackDrag;
    if (!drag) return;

    const distance = event.clientX - drag.startX;
    if (!drag.moved && Math.abs(distance) < TRACK_DRAG_THRESHOLD) return;

    drag.moved = true;
    drag.track.classList.add('is-dragging');
    drag.track.scrollLeft = drag.startScroll - distance;
  };

  onTrackPointerUp = () => {
    const moved = this.trackDrag?.moved;
    this.endTrackDrag();
    if (!moved) return;

    // A drag must not also pick the swatch the pointer happened to end on.
    const swallowClick = (event) => {
      event.stopPropagation();
      event.preventDefault();
    };
    document.addEventListener('click', swallowClick, { capture: true, once: true });
    // No click follows when the button is released off the page; don't eat the next one.
    setTimeout(() => document.removeEventListener('click', swallowClick, { capture: true }), 100);
  };

  endTrackDrag() {
    this.trackDrag?.track.classList.remove('is-dragging');
    this.trackDrag = null;
    document.removeEventListener('pointermove', this.onTrackPointerMove);
    document.removeEventListener('pointerup', this.onTrackPointerUp);
    document.removeEventListener('pointercancel', this.onTrackPointerUp);
  }

  setupSizeGuide() {
    this.addEventListener('click', this.onSizeGuideClick);
    document.addEventListener('product:variant-change', this.onProductVariantChange);
    this.updateSizeGuideHighlight();
  }

  /**
   * A sold-out variant renders the main CTA as the Amp Back in Stock signup
   * (sections/product.liquid, `data-bis-notify`). The button is replaced on
   * every variant change, so the click is delegated from here. `BIS.popup`
   * is Amp's product-page popup; it reads the selected variant from the URL,
   * which this element keeps in step.
   * @param {MouseEvent} event
   */
  onBackInStockClick(event) {
    if (!event.target.closest('[data-bis-notify]')) return;

    event.preventDefault();
    window.BIS?.popup?.show();
  }

  onProductVariantChange(event) {
    if (event.detail?.sectionId && event.detail.sectionId !== this.dataset.section) return;
    this.updateSizeGuideHighlight();
  }

  getSelectedSizeGuideValue() {
    const columns = new Set(
      Array.from(this.querySelectorAll('[data-size-column]')).map((el) =>
        (el.getAttribute('data-size-column') || '').trim().toLowerCase()
      )
    );
    if (!columns.size) return '';

    const checkedValues = Array.from(this.querySelectorAll('variant-selector fieldset input:checked')).map((input) =>
      (input.value || '').trim().toLowerCase()
    );
    return checkedValues.find((value) => columns.has(value)) || '';
  }

  updateSizeGuideHighlight() {
    const selected = this.getSelectedSizeGuideValue();
    this.querySelectorAll('[data-size-column]').forEach((el) => {
      const column = (el.getAttribute('data-size-column') || '').trim().toLowerCase();
      el.classList.toggle('is-selected', Boolean(selected) && column === selected);
    });
  }

  onSizeGuideClick(event) {
    const openButton = event.target.closest('[data-size-guide-open]');
    if (openButton) {
      const dialog = this.querySelector(`#${openButton.getAttribute('aria-controls')}`);
      dialog?.showModal();
      this.updateSizeGuideHighlight();
      return;
    }

    const closeButton = event.target.closest('[data-size-guide-close]');
    if (closeButton) {
      closeButton.closest('dialog')?.close();
      return;
    }

    if (event.target.matches('dialog.variant-select__size-guide-dialog')) {
      event.target.close();
    }
  }

  onResize() {
    clearTimeout(this.resizeTimeout);
    this.resizeTimeout = setTimeout(() => {
      const useVerticalThumbs = this.shouldUseVerticalThumbs();
      if (useVerticalThumbs !== this.lastVerticalThumbs) {
        this.destroySwiper();
        this.initSwiper();
      } else if (this.thumbsSwiper) {
        this.thumbsSwiper.update();
      }
    }, 150);
  }

  shouldUseVerticalThumbs() {
    const thumbnailPosition = this.querySelector('[data-thumbnail-position]')?.dataset.thumbnailPosition || 'bottom';
    return thumbnailPosition === 'left' && this.desktopMediaQuery.matches;
  }

  destroySwiper() {
    if (this.swiper) {
      this.swiper.destroy(true, true);
      this.swiper = undefined;
    }
    if (this.thumbsSwiper) {
      this.thumbsSwiper.destroy(true, true);
      this.thumbsSwiper = undefined;
    }
    this.lastVerticalThumbs = undefined;
  }

  initSwiper() {
    const thumbnailGalleryEl = this.querySelector('.product-media-gallery__thumbnails');
    const thumbnailWrapperEl = this.querySelector('.product-media-gallery__thumbnails-wrapper');
    if (thumbnailGalleryEl) {
      const mainGalleryEl = this.querySelector('.product-media-gallery__main');
      if (mainGalleryEl) {
        this.destroySwiper();

        const useVerticalThumbs = this.shouldUseVerticalThumbs();
        this.lastVerticalThumbs = useVerticalThumbs;

        const prevEl = thumbnailWrapperEl?.querySelector('.swiper-button-prev');
        const nextEl = thumbnailWrapperEl?.querySelector('.swiper-button-next');
        const thumbsOptions = {
          spaceBetween: 9,
          slidesPerView: 5,
          watchOverflow: true,
          watchSlidesProgress: true,
          slideToClickedSlide: true,
          direction: useVerticalThumbs ? 'vertical' : 'horizontal',
          mousewheel: useVerticalThumbs ? { forceToAxis: true } : false,
        };

        if (prevEl && nextEl) {
          thumbsOptions.navigation = {
            prevEl,
            nextEl,
          };
        }

        this.thumbsSwiper = new Swiper(thumbnailGalleryEl, thumbsOptions);

        const mainSlideCount = mainGalleryEl.querySelectorAll('.swiper-wrapper > .swiper-slide').length;
        const mainOptions = {
          spaceBetween: 0,
          loop: mainSlideCount > 1,
          loopedSlides: mainSlideCount,
          thumbs: {
            swiper: this.thumbsSwiper,
          },
        };

        const mainPaginationEl = mainGalleryEl.querySelector('.swiper-pagination');
        if (mainPaginationEl) {
          mainOptions.pagination = {
            el: mainPaginationEl,
          };
        }

        const mainPrevEl = mainGalleryEl.querySelector(':scope > .swiper-button-prev');
        const mainNextEl = mainGalleryEl.querySelector(':scope > .swiper-button-next');
        if (mainPrevEl && mainNextEl) {
          mainOptions.navigation = {
            prevEl: mainPrevEl,
            nextEl: mainNextEl,
          };
        }

        this.swiper = new Swiper(mainGalleryEl, mainOptions);

        this.thumbsSwiper.update();
        return;
      }
    }

    const carouselGalleryEl = this.querySelector('.product-media-gallery__carousel');
    if (carouselGalleryEl) {
      this.destroySwiper();
      const carouselOptions = {
        autoHeight: true,
        loop: true,
        direction: 'horizontal',
      };

      const carouselPaginationEl = carouselGalleryEl.querySelector('.swiper-pagination');
      if (carouselPaginationEl) {
        carouselOptions.pagination = {
          el: carouselPaginationEl,
        };
      }

      const carouselPrevEl = carouselGalleryEl.querySelector('.swiper-button-prev');
      const carouselNextEl = carouselGalleryEl.querySelector('.swiper-button-next');
      if (carouselPrevEl && carouselNextEl) {
        carouselOptions.navigation = {
          prevEl: carouselPrevEl,
          nextEl: carouselNextEl,
        };
      }

      this.swiper = new Swiper(carouselGalleryEl, carouselOptions);
    }
  }

  get variantSelector() {
    return this.querySelector('variant-selector');
  }

  get quantitySelector() {
    return this.querySelector('quantity-selector');
  }

  get selectedOptionValues() {
    if (this.variantSelector.dataset.pickerType === 'dropdown') {
      const list = Array.from(this.variantSelector.querySelectorAll('select')).map(
        (select) => select.options[select.selectedIndex].dataset.optionValueId
      );
      return list;
    } else {
      const list = Array.from(this.variantSelector.querySelectorAll('fieldset input:checked')).map(
        ({ dataset }) => dataset.optionValueId
      );
      return list;
    }
  }

  getSelectedVariant(html) {
    const selectedVariant = html.querySelector('[data-selected-variant]')?.innerHTML;
    return !!selectedVariant ? JSON.parse(selectedVariant) : null;
  }

  onVariantChange(e) {
    const productUrlChanged = e.target?.dataset?.productUrl ? (e.target?.dataset?.productUrl !== this.dataset.url) : false;
    const productUrl = e.target?.dataset?.productUrl || this.dataset.url;
    this.renderSection(productUrlChanged, productUrl);
  }

  onQuantitySelectorEvent(e) {
    const quantityInput = this.quantitySelector.querySelector('input[type="number"]');
    let currentValue = parseInt(quantityInput.value);
    const minValue = parseInt(quantityInput.getAttribute('min')) || 0;
    const maxValue = parseInt(quantityInput.getAttribute('max')) || Infinity;

    if (e.target.name === 'minus' && currentValue > minValue) {
      quantityInput.value = currentValue - 1;
    } else if (e.target.name === 'plus' && currentValue < maxValue) {
      quantityInput.value = currentValue + 1;
    } else if (e.type === 'change') {
      if (currentValue < minValue) {
        quantityInput.value = minValue;
      } else if (currentValue > maxValue) {
        quantityInput.value = maxValue;
      }
    }
  }

  updateMedia(variantFeaturedMediaId) {
    if (!variantFeaturedMediaId) return;
    const mediaSlide = this.querySelector(`.swiper-slide[data-media-id="${variantFeaturedMediaId}"]`);
    if (!mediaSlide) return;

    // Speed 0 jumps straight to the variant's media; animating would scroll
    // the shopper past every image in between.
    const slideIndex = parseInt(mediaSlide.dataset.mediaIndex);
    if (this.swiper?.params?.loop) {
      this.swiper.slideToLoop(slideIndex, 0);
    } else {
      this.swiper?.slideTo(slideIndex, 0);
    }
  }

  /**
   * Swaps in the gallery and zoom-modal media the server filtered for the new
   * variant (alt-text match, see component-product-media-filter), then rebuilds
   * Swiper. Falls back to jumping to the variant's media when the response has
   * no gallery.
   */
  updateGallery(html, variantFeaturedMediaId) {
    const liveGallery = this.querySelector('media-gallery');
    const newGallery = html.querySelector('media-gallery');
    if (!liveGallery || !newGallery) {
      this.updateMedia(variantFeaturedMediaId);
      return;
    }

    this.destroySwiper();
    liveGallery.replaceWith(newGallery);

    const liveModalContent = this.querySelector('product-media-modal .product-media-modal__content');
    const newModalContent = html.querySelector('product-media-modal .product-media-modal__content');
    if (liveModalContent && newModalContent) {
      liveModalContent.innerHTML = newModalContent.innerHTML;
    }

    // The modal binds its openers once at construction, so the swapped-in
    // zoom triggers need their own click handlers.
    newGallery.querySelectorAll('.light-box-zoom-trigger').forEach((trigger) => {
      trigger.addEventListener('click', () => {
        const modal = document.querySelector(trigger.getAttribute('data-modal'));
        if (modal) modal.showModal(trigger);
      });
    });

    this.initSwiper();
  }

  updateURL(variantId) {
    // this.querySelector('share-button')?.updateUrl(
    //   `${window.shopUrl}${url}${variantId ? `?variant=${variantId}` : ''}`
    // );

    // Don't update URL if this is in a modal/quick-add context
    if (this.dataset.updateUrl === 'false') return;

    if (!window.location.pathname.includes('/products/')) return;
    window.history.replaceState({}, '', `${this.dataset.url}${variantId ? `?variant=${variantId}` : ''}`);
  }

  updateSourceFromDestination = (html, id) => {
    const source = html.getElementById(`${id}`);
    if (!source) return;
    this.querySelectorAll(`#${CSS.escape(id)}, [data-variant-update="${id}"]`).forEach((destination) => {
      destination.innerHTML = source.innerHTML;
    });
  };

  /**
   * Updates the image-swatch picker in place instead of replacing its markup.
   *
   * The rail (`.variant-select__images-track`) is a native horizontal scroller, so
   * rebuilding it via innerHTML throws away its scrollLeft and the focused radio —
   * on mobile that reads as the rail jumping back to the first flavor on every tap.
   * Syncing state onto the existing nodes keeps the scroll position and the images.
   *
   * @param {Document} html - The freshly fetched section markup.
   * @returns {boolean} True when the live picker was updated; false when the caller must fall back to a full replace.
   */
  syncImagePicker(html) {
    if (this.querySelectorAll('variant-selector').length !== 1) {
      return false;
    }

    const destination = this.variantSelector;
    if (destination?.dataset.pickerType !== 'image') {
      return false;
    }

    const source = html.getElementById(`variant-selector-${this.dataset.section}`);
    if (!source) {
      return false;
    }

    const sourceInputs = Array.from(source.querySelectorAll('.variant-select__image-radio'));
    const liveInputs = Array.from(destination.querySelectorAll('.variant-select__image-radio'));
    if (sourceInputs.length === 0 || sourceInputs.length !== liveInputs.length) {
      return false;
    }

    // A mismatched id list means the option values themselves changed, so the
    // markup has to be rebuilt rather than patched.
    if (sourceInputs.some((input, index) => input.id !== liveInputs[index].id)) {
      return false;
    }

    for (const [index, sourceInput] of sourceInputs.entries()) {
      const liveInput = liveInputs[index];

      // These nodes come from DOMParser and were never interacted with, so read the
      // attribute rather than the `checked` property.
      liveInput.checked = sourceInput.hasAttribute('checked');
      liveInput.value = sourceInput.value;
      this.copyDataAttribute(liveInput, sourceInput, 'data-product-url');
      this.copyDataAttribute(liveInput, sourceInput, 'data-option-value-id');

      const sourceLabel = sourceInput.nextElementSibling;
      const liveLabel = liveInput.nextElementSibling;
      if (!sourceLabel || !liveLabel) {
        continue;
      }

      // Carries `is-unavailable`.
      liveLabel.className = sourceLabel.className;

      const sourceName = sourceLabel.querySelector('.variant-select__image-name');
      const liveName = liveLabel.querySelector('.variant-select__image-name');
      if (sourceName && liveName) {
        liveName.textContent = sourceName.textContent;
      }

      // The sold-out span is the only `.visually-hidden` node inside the label.
      const sourceSoldOut = sourceLabel.querySelector('.visually-hidden');
      const liveSoldOut = liveLabel.querySelector('.visually-hidden');
      if (sourceSoldOut && !liveSoldOut) {
        liveLabel.append(sourceSoldOut.cloneNode(true));
      }
      if (!sourceSoldOut && liveSoldOut) {
        liveSoldOut.remove();
      }
    }

    this.syncTextNodes(source, destination, '.variant-select__images-current');
    this.syncTextNodes(source, destination, '.variant-select__images-group legend');

    // component-data-layer.js reads the live node, not the fetched document.
    this.syncTextNodes(source, destination, '[data-selected-variant]');

    return true;
  }

  /**
   * Copies one data attribute from the fetched node onto the live node.
   *
   * Assigning through `dataset` would stringify a missing source value to the
   * literal "undefined", which is truthy and would survive the checks in
   * `onVariantChange` and `selectedOptionValues` all the way into the fetch URL.
   *
   * @param {Element} live - Node in the live picker.
   * @param {Element} source - Matching node in the fetched markup.
   * @param {string} attribute - Fully qualified attribute name, e.g. `data-product-url`.
   * @returns {void}
   */
  copyDataAttribute(live, source, attribute) {
    const value = source.getAttribute(attribute);
    if (value === null) {
      live.removeAttribute(attribute);
      return;
    }

    live.setAttribute(attribute, value);
  }

  /**
   * Copies textContent from the fetched markup onto the matching live nodes.
   * @param {Element} source - Root of the fetched picker.
   * @param {Element} destination - Root of the live picker.
   * @param {string} selector - Selector matching text-only nodes in both roots.
   * @returns {void}
   */
  syncTextNodes(source, destination, selector) {
    const sourceNodes = source.querySelectorAll(selector);
    const liveNodes = destination.querySelectorAll(selector);
    if (sourceNodes.length !== liveNodes.length) {
      return;
    }

    for (const [index, sourceNode] of sourceNodes.entries()) {
      liveNodes[index].textContent = sourceNode.textContent;
    }
  }

  updateVariantInputs(variantId) {
    this.querySelectorAll(`#product-form-${this.dataset.section}`).forEach((productForm) => {
      const input = productForm.querySelector('input[name="id"]');
      input.value = variantId ?? '';
    });
  }

  renderSection(productUrlChanged, productUrl) {
    this.abortController?.abort();
    this.abortController = new AbortController();

    // If the section is in a modal, use the original section id without the modal suffix
    const sectionId = this.dataset.updateUrl === 'false' ? this.dataset.section.split('-modal')[0] : this.dataset.section;
    fetch(`${productUrl}?option_values=${this.selectedOptionValues}&section_id=${sectionId}`, {
      signal: this.abortController.signal,
    })
      .then((response) => response.text())
      .then((responseText) => {
        // If the section is in a modal, replace the original section id with the modal section id
        if (this.dataset.updateUrl === 'false') {
          responseText = responseText.replaceAll(this.dataset.section.split('-modal')[0], this.dataset.section);
        }

        // Parse the response text into an HTML document
        const html = new DOMParser().parseFromString(responseText, 'text/html');
        const variant = this.getSelectedVariant(html);
        if (productUrlChanged) {
          // If the product url has changed, replace the current section with the new section
          const productInfo = html.querySelector('product-info');
          this.replaceWith(productInfo);
          productInfo.updateURL(variant?.id);
          document.dispatchEvent(
            new CustomEvent('product:variant-change', {
              detail: { sectionId: productInfo.dataset.section, variant },
            })
          );
        } else {
          this.updateGallery(html, variant?.featured_media?.id);
          this.updateURL(variant?.id);
          this.updateVariantInputs(variant?.id);
          this.updateSourceFromDestination(html, `add-to-cart-container-${this.dataset.section}`);
          if (!this.syncImagePicker(html)) {
            this.updateSourceFromDestination(html, `variant-selector-${this.dataset.section}`);
          }
          this.updateSourceFromDestination(html, `price-${this.dataset.section}`);
          this.updateSourceFromDestination(html, `afterpay-${this.dataset.section}`);
          this.updateSourceFromDestination(html, `sku-${this.dataset.section}`);
          this.updateSourceFromDestination(html, `inventory-${this.dataset.section}`);
          this.updateSourceFromDestination(html, `fbt-current-${this.dataset.section}`);
          document.dispatchEvent(
            new CustomEvent('product:variant-change', {
              detail: { sectionId: this.dataset.section, variant },
            })
          );
        }
      })
      .catch((error) => {
        if (error.name === 'AbortError') {
          console.log('Fetch aborted by user');
        } else {
          console.error(error);
        }
      });
  }
}

if (!customElements.get('product-info')) {
  customElements.define('product-info', ProductInfo);
}

export class CustomVariantSelect extends HTMLElement {
  constructor() {
    super();
    this.onTriggerClick = this.onTriggerClick.bind(this);
    this.onOptionClick = this.onOptionClick.bind(this);
    this.onDocumentClick = this.onDocumentClick.bind(this);
  }

  connectedCallback() {
    this.trigger = this.querySelector('.variant-block__trigger');
    this.list = this.querySelector('.variant-block__list');
    this.select = this.querySelector('select');
    if (!this.trigger || !this.list || !this.select) return;

    this.trigger.addEventListener('click', this.onTriggerClick);
    this.list.addEventListener('click', this.onOptionClick);
    document.addEventListener('click', this.onDocumentClick);
  }

  disconnectedCallback() {
    document.removeEventListener('click', this.onDocumentClick);
  }

  get isOpen() {
    return this.classList.contains('is-open');
  }

  open() {
    this.classList.add('is-open');
    this.trigger.setAttribute('aria-expanded', 'true');
    this.list.hidden = false;
  }

  close() {
    this.classList.remove('is-open');
    this.trigger.setAttribute('aria-expanded', 'false');
    this.list.hidden = true;
  }

  onTriggerClick(e) {
    e.preventDefault();
    this.isOpen ? this.close() : this.open();
  }

  onDocumentClick(e) {
    if (!this.contains(e.target)) this.close();
  }

  onOptionClick(e) {
    const option = e.target.closest('.variant-block__option');
    if (!option) return;

    const value = option.dataset.value;
    const matchingOption = Array.from(this.select.options).find((opt) => opt.value === value);
    if (!matchingOption) return;

    this.select.value = value;
    if (option.dataset.productUrl) this.select.dataset.productUrl = option.dataset.productUrl;
    this.close();
    this.select.dispatchEvent(new Event('change', { bubbles: true }));
  }
}

if (!customElements.get('custom-variant-select')) {
  customElements.define('custom-variant-select', CustomVariantSelect);
}